import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { IndustryEnum } from '@dashboard/enums/industry';

import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { formatPrice } from '@dashboard/services/formatting';
import plans from '@dashboard/services/plans';
import { fetchInfinite } from '@dashboard/services/request';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { FormElement, Label, Select, TextInput } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';
import { InfoTooltip } from '@dashboard/components/Tooltip';

import Table from '../components/Table';

import { getRenewalStatus } from './Merchant';

const planHandles = plans.reduce((acc, plan) => {
  if (!acc.find(p => p.handle === plan.handle)) {
    acc.push(plan);
  }

  return acc;
}, []);

const MerchantsUsage = () => {
  const { token } = useAuth();
  const history = useHistory();

  const urlQuery = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput();
  const [sortBy, setSortBy] = useState(urlQuery.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(urlQuery.sortDirection || -1);
  const [limit] = useState(20);
  const [plan, setPlan] = useState(urlQuery.plan || '');
  const [billingPeriod, setBillingPeriod] = useState(urlQuery.billingPeriod || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState(
    urlQuery.subscriptionStatus || 'active',
  );
  const [industry, setIndustry] = useState(urlQuery.industry || '');
  const [hasCustomPricing, setHasCustomPricing] = useState(urlQuery.hasCustomPricing || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(plan && { plan }),
        ...(billingPeriod && { billingPeriod }),
        ...(subscriptionStatus && { subscriptionStatus }),
        ...(industry && { industry }),
        ...(hasCustomPricing && { hasCustomPricing }),
        ...(search && { search }),
      }),
    });
  }, [sortBy, sortDirection, plan, subscriptionStatus, industry, hasCustomPricing, search, billingPeriod]);

  const query = {
    ...(plan && {
      'plan.handle': plan,
    }),
    ...(billingPeriod && {
      'plan.billingPeriod': billingPeriod,
    }),
    ...(subscriptionStatus && {
      subscriptionStatus: subscriptionStatus,
    }),
    ...(industry && { industry: industry }),
    ...(hasCustomPricing === 'true' && {
      'plan.volumeLimit': JSON.stringify({ $exists: true, $ne: null }),
    }),
    ...(hasCustomPricing === 'false' && {
      $or: JSON.stringify([
        { 'plan.volumeLimit': { $exists: false } },
        { 'plan.volumeLimit': null },
      ]),
    }),
    search,
    limit,
    sort: JSON.stringify({
      [sortBy]: sortDirection,
    }),
  };

  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['merchants', query],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/merchants-usage?${stringify({
          ...query,
          limit: limit,
          skip: pageParam * limit,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    retryDelay: 1000,
    staleTime: 1000 * 60 * 60 * 24,
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <React.Fragment>
      <div className="flex justify-end items-end px-4">
        <div className="pb-0 flex gap-x-2">
          <FormElement className="!mb-0">
            <Label htmlFor="industry">Store focus</Label>
            <Select
              name="industry"
              label="Store focus"
              onChange={e => setIndustry(e.target.value)}
              className="w-32"
            >
              <option value="">All</option>
              {Object.values(IndustryEnum).map(currentAccountType => (
                <option
                  key={currentAccountType}
                  value={currentAccountType}
                  selected={currentAccountType === industry}
                >
                  {upperFirst(currentAccountType)}
                </option>
              ))}
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="plan">Plan</Label>
            <Select
              name="plan"
              label="Plan"
              onChange={e => {
                setPlan(e.target.value);

                if (!subscriptionStatus) {
                  setSubscriptionStatus('active');
                }
              }}
              className="w-32"
              value={plan}
            >
              <option value="">All</option>
              {planHandles.map(plan => (
                <option key={plan.handle} value={plan.handle} className="">
                  {plan.title}
                </option>
              ))}
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="billingPeriod">Billing period</Label>
            <Select
              name="billingPeriod"
              label="Billing period"
              onChange={e => {
                setBillingPeriod(e.target.value);

                if (!subscriptionStatus) {
                  setSubscriptionStatus('active');
                }
              }}
              className="w-32"
              value={billingPeriod}
            >
              <option value="">All</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="subscriptionStatus">Subscription status</Label>
            <Select
              name="subscriptionStatus"
              label="Subscription status"
              onChange={e => setSubscriptionStatus(e.target.value)}
              className="!w-40"
              value={subscriptionStatus}
            >
              <option value="">All</option>
              <option value="trialing">Trialing</option>
              <option value="active">Active</option>
              <option value="trial_expired">Trial expired</option>
              <option value="canceled">Canceled</option>
              <option value="legacy">Legacy</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="hasCustomPricing">Custom pricing</Label>
            <Select
              name="hasCustomPricing"
              label="Custom pricing"
              onChange={e => setHasCustomPricing(e.target.value)}
              className="w-32"
              value={hasCustomPricing}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </FormElement>

          <FormElement className="!mb-0">
            <Label htmlFor="search">Search</Label>
            <div className="w-64">
              <TextInput
                border
                name="search"
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search by name, email, ID"
                value={searchInput}
              />
            </div>
          </FormElement>
        </div>
      </div>

      <Table.Table>
        <Table.Head>
          <Table.Th
            onSort={onSort}
            sortKey="displayName"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Store
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="industry"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Store focus
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="planHandle"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Plan
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="monthlyRecurringRevenue"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            MRR
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="totalRevenueThisMonth"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Revenue this month
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="totalRevenueLastMonth"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Revenue last month
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="totalRevenueLast12Months"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Revenue last 12 months
          </Table.Th>
          <Table.Th onSort={onSort} sortKey="pages" sortBy={sortBy} sortDirection={sortDirection}>
            Pages
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="teamMembersSessions"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Sessions <InfoTooltip>Total sessions for all team members combined</InfoTooltip>
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="hasCustomDomain"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Custom domain
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="teamMembersCount"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Team members
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="webhooksCount"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Webhooks
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="checkoutAbandonment.collectEmails"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Collect emails?
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="checkoutAbandonment.sendEmails"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Send emails?
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Signed up at
          </Table.Th>
          <Table.Th
            onSort={onSort}
            sortKey="currentPeriodEnds"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Status
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(merchant => (
                <Table.Row key={merchant.id}>
                  <Table.Td className="!max-w-xs">
                    <Link to={`/admin/merchants/${merchant._id}`}>
                      {merchant.displayName || merchant.name || 'N/A'}
                    </Link>
                  </Table.Td>

                  <Table.Td>
                    {merchant.industry ? upperFirst(merchant.industry.replace(/-/g, ' ')) : 'N/A'}
                  </Table.Td>

                  <Table.Td>
                    {merchant?.plan?.title}
                    {merchant?.plan?.billingPeriod && (
                      <span> - {merchant?.plan?.billingPeriod}</span>
                    )}
                  </Table.Td>

                  <Table.Td>
                    {formatPrice({
                      amount: merchant.monthlyRecurringRevenue,
                      currency: 'usd',
                      style: 'currency',
                    })}{' '}
                    / month
                  </Table.Td>

                  <Table.Td>
                    {formatPrice({
                      amount: merchant.totalRevenueThisMonth,
                      currency: 'usd',
                      style: 'currency',
                    })}
                  </Table.Td>

                  <Table.Td className="text-gray-500">
                    {formatPrice({
                      amount: merchant.totalRevenueLastMonth,
                      currency: 'usd',
                      style: 'currency',
                    })}
                  </Table.Td>

                  <Table.Td>
                    {formatPrice({
                      amount: merchant.totalRevenueLast12Months,
                      currency: 'usd',
                      style: 'currency',
                    })}
                  </Table.Td>

                  <Table.Td>{merchant.pages}</Table.Td>

                  <Table.Td>{merchant.teamMembersSessions}</Table.Td>

                  <Table.Td>{merchant.hasCustomDomain ? 'Yes' : 'No'}</Table.Td>

                  <Table.Td>{merchant.teamMembersCount}</Table.Td>
                  <Table.Td>{merchant.webhooksCount}</Table.Td>
                  <Table.Td>{merchant.checkoutAbandonment?.collectEmails ? 'Yes' : 'No'}</Table.Td>
                  <Table.Td>{merchant.checkoutAbandonment?.sendEmails ? 'Yes' : 'No'}</Table.Td>

                  <Table.Td>{format(new Date(merchant.createdAt), 'd MMM, yyyy, HH:mm')}</Table.Td>

                  <Table.Td>
                    <div>{getRenewalStatus(merchant, true)}</div>
                  </Table.Td>
                </Table.Row>
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </React.Fragment>
  );
};

export default MerchantsUsage;
